/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useCallback, useMemo, useRef, useState, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'

// Components
import {
    Box,
    IconButton,
    Link as ChakraLink,
    useBreakpointValue,
    useDisclosure,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {HeartIcon, HeartSolidIcon} from '../icons'
import Link from '@salesforce/retail-react-app/app/components/link'
import withRegistration from '../with-registration'
import ProductTileDetails from './partials/product-tile-details'
import ProductTileMedia from './partials/product-tile-media'
import ProductTileMobileQuickView from './partials/product-tile-mobile-quickview'
import {ProductTileSkeleton} from './partials/product-tile-skeleton'

// Hooks
import {useProduct, usePromotions} from '@salesforce/commerce-sdk-react'
import {useWishlistActions} from '../../hooks/use-wishlist-actions'
import {useVariationByAttributeValues} from '../../hooks/use-variant-by-attribute-values'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'
import {useBrand} from '../../contexts/brand-context'
import useProductTileCart from './hooks/use-product-tile-cart'
import useProductTileHover from './hooks/use-product-tile-hover'
import useWishlistCount from './hooks/use-wishlist-count'
import useProductTileMode from './hooks/use-product-tile-mode'

// Other
import {
    PRODUCT_TILE_IMAGE_VIEW_TYPE,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID
} from '@salesforce/retail-react-app/app/constants'
import {productUrlBuilder, rebuildPathWithParams} from '../../utils/url'
import {getPriceData, isGiftCardProduct} from '../../utils/product-utils'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {PRODUCT_BADGE_DETAILS} from '@salesforce/retail-react-app/app/constants'
import {useMiniCartContext} from '../../contexts/mini-cart-context'
import {useAddToCartConfirmation} from '../../contexts/add-to-cart-confirmation-context'
import {
    buildImageUrl,
    getProductPromotionIds,
    getProductTileImages,
    buildTileMediaItems
} from './utils/product-tile-utils'
import productTilePropTypes from './utils/product-tile-proptypes'

const findVariantByProductId = (product, productId) =>
    product?.variants?.find((variant) => variant?.productId === productId)

const IconButtonWithRegistration = withRegistration(IconButton, {isWishlistAction: true})

export const Skeleton = ProductTileSkeleton

/**
 * The ProductTile is a simple visual representation of a
 * product object. It will show it's default image, name and price.
 * It also supports favourite products, controlled by a heart icon.
 */
const ProductTile = forwardRef((props, ref) => {
    const location = useLocation()

    const {
        dynamicImageProps,
        videoProps,
        enableFavourite = true,
        imageViewType = PRODUCT_TILE_IMAGE_VIEW_TYPE,
        isFavourite,
        onFavouriteToggle,
        product = {},
        selectableAttributeId = PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID,
        badgeDetails = PRODUCT_BADGE_DETAILS,
        isRefreshingData = false,
        showSwatches,
        showVideo = false,
        showDetails = true,
        showProductLikes = true,
        showQV = true,
        isShopTheLook = false,
        isOOS = false,
        setQuickViewProduct,
        setQuickViewWishlistInfo,
        setMobileQuickViewOpen,
        isWishlistPage = false,
        disableImageScroll = false,
        showScrollHint = false,
        overwriteProductName = null,
        responsiveMediaBlock = null,
        variant: stylingVariant,
        onClick,
        onQuickViewClose,
        isLayout = false,
        // Extract CMS styling props to prevent them from being passed to DOM
        productTitleStyle,
        productPriceStyle,
        // GDI carousel-driven on/off toggles. Each entry is a Chakra responsive
        // `display` value applied to its corresponding subcomponent. Default
        // undefined → no override → existing behavior is preserved (PLP /
        // recommendations / wishlist all leave this prop unset).
        responsiveDisplay,
        // GDI carousel-forced tile mode ('quick-view' | 'quick-add'), passed
        // as a Chakra responsive object. When set, it overrides the
        // screensize-based `useProductTileMode` decision for the Quick Add
        // overlay.
        forceTileMode,
        ...rest
    } = props
    const {imageGroups, productId: initialProductId, id, representedProduct, variants} = product
    const tileRef = useRef(null)
    const setTileRef = useCallback(
        (node) => {
            tileRef.current = node
            if (typeof ref === 'function') {
                ref(node)
            } else if (ref) {
                ref.current = node
            }
        },
        [ref]
    )

    // For variation groups, productId may be undefined, so we use id as fallback
    // This ensures QuickView can fetch product data correctly
    const effectiveInitialProductId = initialProductId || id
    const [productId, setProductId] = useState(effectiveInitialProductId)

    // Store the ORIGINAL productId for wishlist removal (the one from the wishlist item)
    // This is the product.id value that never changes, even when user selects different variants
    const wishlistProductId = id

    // Ensure productId is always valid - if it becomes undefined, reset it
    useEffect(() => {
        if (!productId && effectiveInitialProductId) {
            setProductId(effectiveInitialProductId)
        }
    }, [productId, effectiveInitialProductId])
    const {isOpen, onOpen, onToggle, onClose} = useDisclosure()
    const {onOpen: onAddToCartModalOpen, onAutoClose: onAddToCartModalClose} = useMiniCartContext()
    const {setConfirmationTrigger} = useAddToCartConfirmation()

    const intl = useIntl()
    const {currency} = useCurrency()
    const isDesktop = useBreakpointValue({base: false, lg: true})

    const [isHovered, setIsHovered] = useState(false)
    const [isQuickViewOpen, setIsQuickViewOpen] = useState(false)

    // Mobile Quickview State
    const [mobileQuickViewOpen, setMobileQuickViewOpenInternal] = useState(false)

    // Track variant selected in QuickView to update tile price
    const [selectedVariantId, setSelectedVariantId] = useState(null)

    // Track product selected in QuickView
    const [qvProduct, setQvProduct] = useState(null)

    // Track size/length selections made in QuickView so productUrl carries them to PDP
    const [qvLocalSelections, setQvLocalSelections] = useState({})

    const selectedTileProductId = productId || effectiveInitialProductId
    const qvProductMatchesSelectedProduct =
        qvProduct &&
        (qvProduct.id === selectedTileProductId || qvProduct.productId === selectedTileProductId)
    const shouldFetchSelectedTileProduct =
        !!selectedTileProductId &&
        selectedTileProductId !== effectiveInitialProductId &&
        !qvProductMatchesSelectedProduct
    const {
        data: selectedTileProduct,
        isLoading: isSelectedTileProductLoading,
        isFetching: isSelectedTileProductFetching
    } = useProduct(
        {
            parameters: {
                id: selectedTileProductId,
                perPricebook: true,
                expand: ['availability', 'images', 'prices', 'promotions', 'variations'],
                allImages: true
            }
        },
        {
            enabled: shouldFetchSelectedTileProduct,
            keepPreviousData: true
        }
    )
    const selectedTileProductMatchesSelectedProduct =
        selectedTileProduct &&
        (selectedTileProduct.id === selectedTileProductId ||
            selectedTileProduct.productId === selectedTileProductId)
    const isSelectedTileProductPriceLoading =
        shouldFetchSelectedTileProduct &&
        !selectedTileProductMatchesSelectedProduct &&
        (isSelectedTileProductLoading || isSelectedTileProductFetching)

    // Wishlist hook
    const {isInWishlist, toggleWishlist, removeItemFromWishlist} = useWishlistActions()

    const hasLengthVariant = Array.isArray(product?.variants)
        ? product.variants.some(
              (variant) =>
                  variant?.variationValues &&
                  typeof variant.variationValues === 'object' &&
                  'length' in variant.variationValues &&
                  variant.variationValues.length
          )
        : false

    const isMasterVariant = !!variants
    const initialVariationValue =
        isMasterVariant && !!representedProduct
            ? variants?.find((variant) => variant.productId == product.representedProduct.id)
                  ?.variationValues?.[selectableAttributeId]
            : undefined

    // I added this because the representedProduct is available only on the useProductSearch hook so the price will not be properly rendered
    const defaultColorVariationValue =
        isMasterVariant && !initialVariationValue ? product?.variationValues?.color : undefined

    let [colorValue, setColorValue] = useState(initialVariationValue || defaultColorVariationValue)
    const [isNotifyMeModalOpen, setIsNotifyMeModalOpen] = useState(false)
    const [isShopSimilarModalOpen, setIsShopSimilarModalOpen] = useState(false)

    const productObject = product
    const pricingProduct =
        (qvProductMatchesSelectedProduct && qvProduct) ||
        (selectedTileProductMatchesSelectedProduct && selectedTileProduct) ||
        product
    const hasTileVideo = showVideo && productObject?.representedProduct?.c_mediaProductTile

    const {wishlistCount, setWishlistCount, isLoadingFreshLikes, triggerFreshLikesFetch} =
        useWishlistCount({
            product,
            productId: productId || id,
            locationPathname: location.pathname
        })

    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const {isGarage, isDynamite} = useBrand()
    const isProductTileMediaCarouselEnabled =
        getSiteEnvVarValue(appConfig, 'PRODUCT_TILE_MEDIA_CAROUSEL_ENABLED', site?.id) === 'true'

    const isWishlistEnabled = productObject?.c_enableWishlistCount || false

    // Skip QuickView (show "Add to bag" button) ONLY for true variant products in Wishlist
    // - Variants: have type.variant === true (specific color/size already selected)
    // - Masters/VariationGroups: have type.variationGroup === true or type.master === true (need attribute selection)
    const isActualVariant = product?.type?.variant === true

    const isGiftCard = isGiftCardProduct(productObject)

    const ShouldSkipQuickView = (isActualVariant && isWishlistPage) || isGiftCard

    // Determine wishlist status
    const isProductInWishlist =
        isFavourite !== undefined ? isFavourite : isInWishlist(productId || id)

    // Handle favourite toggle
    const handleFavouriteClick = async () => {
        if (onFavouriteToggle) {
            // Use external handler (como ProductList faz)
            await onFavouriteToggle(!isProductInWishlist)
        } else {
            // Use internal logic (fallback automático)
            const productObj = {productId: productId || id, ...product}
            await toggleWishlist(productObj, tileRef?.current)
        }

        // Update local count
        if (isProductInWishlist) {
            setWishlistCount((prev) => Math.max(0, prev - 1))
        } else {
            setWishlistCount((prev) => prev + 1)
        }
    }

    // Primary image for the tile, the image is determined from the product and selected variation attributes.
    const image = useMemo(
        () =>
            getProductTileImages({
                product,
                imageGroups,
                selectableAttributeId,
                initialVariationValue,
                imageViewType,
                colorValue,
                productId
            }),
        [
            product,
            imageGroups,
            selectableAttributeId,
            initialVariationValue,
            imageViewType,
            colorValue,
            productId
        ]
    )
    // Primary URL user to wrap the ProduceTile.
    // Note: We no longer include color in URL params for SEO purposes.
    // Color is determined by the variation group productId in the URL path.
    const productUrl = useMemo(() => {
        // If QuickView has fetched full product data, use it (has c_relativeUrl)
        let baseUrl
        if (qvProduct) {
            baseUrl = productUrlBuilder(qvProduct)
        } else {
            // If productId was updated by QuickView swatch click (immediate),
            // reconstruct the URL from the current product's URL pattern
            let productForUrl = product
            if (product?.c_relativeUrl && productId) {
                const urlMatch = product.c_relativeUrl.match(/^(.*\/)([^/]+)\.html$/)
                const urlProductId = urlMatch?.[2]
                if (urlProductId && urlProductId !== productId) {
                    productForUrl = {
                        ...product,
                        c_relativeUrl: `${urlMatch[1]}${productId}.html`
                    }
                }
            }
            baseUrl = productUrlBuilder(productForUrl)
        }

        // Append size/length selections from QV as query params so PDP pre-selects them
        const nonColorSelections = Object.fromEntries(
            Object.entries(qvLocalSelections).filter(([key]) => key !== 'color')
        )
        if (baseUrl && Object.keys(nonColorSelections).length > 0) {
            return rebuildPathWithParams(baseUrl, nonColorSelections)
        }
        return baseUrl
    }, [product, qvProduct, productId, qvLocalSelections])

    // NOTE: variationAttributes are only defined for master/variant type products.
    // const variationAttributes = useMemo(() => getDecoratedVariationAttributes(product), [product])

    // ProductTile is used by two components, RecommendedProducts and ProductList.
    // RecommendedProducts provides a localized product name as `name` and non-localized product
    // name as `productName`. ProductList provides a localized name as `productName` and does not
    // use the `name` property.
    const localizedProductName = overwriteProductName || (product.name ?? product.productName)

    const selectedVariationValue = useMemo(() => {
        if (colorValue) return colorValue

        const productIdVariant =
            findVariantByProductId(pricingProduct, productId) ||
            findVariantByProductId(product, productId) ||
            pricingProduct?.variationGroups?.find((group) => group?.productId === productId) ||
            product?.variationGroups?.find((group) => group?.productId === productId)

        return (
            productIdVariant?.variationValues?.[selectableAttributeId] ||
            initialVariationValue ||
            defaultColorVariationValue
        )
    }, [
        colorValue,
        pricingProduct,
        product,
        productId,
        selectableAttributeId,
        initialVariationValue,
        defaultColorVariationValue
    ])

    const productWithFilteredVariants = useMemo(() => {
        const fallbackVariationValue = initialVariationValue || defaultColorVariationValue
        const getVariantsForValue = (variationValue) =>
            variationValue
                ? pricingProduct?.variants?.filter(
                      ({variationValues}) =>
                          variationValues?.[selectableAttributeId] === variationValue
                  )
                : pricingProduct?.variants
        const selectedVariants = getVariantsForValue(selectedVariationValue)
        const fallbackVariants = getVariantsForValue(fallbackVariationValue)
        const variants = selectedVariants?.length
            ? selectedVariants
            : fallbackVariants?.length
            ? fallbackVariants
            : pricingProduct?.variants
        const effectiveVariationValue = selectedVariants?.length
            ? selectedVariationValue
            : fallbackVariationValue

        return {
            ...pricingProduct,
            variants,
            variationValues: {
                ...pricingProduct?.variationValues,
                ...(effectiveVariationValue
                    ? {[selectableAttributeId]: effectiveVariationValue}
                    : {})
            }
        }
    }, [
        pricingProduct,
        selectableAttributeId,
        selectedVariationValue,
        initialVariationValue,
        defaultColorVariationValue
    ])

    // Pricing is dynamic! Ensure we are showing the right price for the selected variation attribute
    // value. When a variant is selected in QuickView, use that variant's price data.
    const priceData = useMemo(() => {
        // If a variant is selected in QuickView, calculate price for that specific variant
        if (selectedVariantId) {
            const selectedVariant =
                findVariantByProductId(pricingProduct, selectedVariantId) ||
                findVariantByProductId(product, selectedVariantId)

            if (selectedVariant) {
                const selectedSearchVariant = findVariantByProductId(product, selectedVariantId)
                // Use variant's own promotions, or fallback to product-level promotions
                const variantWithPromotions = {
                    ...selectedSearchVariant,
                    ...selectedVariant,
                    productPromotions:
                        selectedVariant.productPromotions ||
                        selectedSearchVariant?.productPromotions ||
                        pricingProduct?.productPromotions ||
                        product?.productPromotions,
                    c_newArrivalDate:
                        selectedVariant?.c_newArrivalDate ||
                        selectedSearchVariant?.c_newArrivalDate ||
                        pricingProduct?.c_newArrivalDate ||
                        product?.c_newArrivalDate
                }
                return getPriceData(variantWithPromotions)
            }
        }
        // No variant selected - show lowest price across all variants
        return getPriceData(productWithFilteredVariants)
    }, [
        productWithFilteredVariants,
        selectedVariantId,
        pricingProduct,
        product
    ])

    // Retrieve product badges
    const filteredLabels = useMemo(() => {
        const labelsMap = new Map()
        if (product?.representedProduct) {
            badgeDetails.forEach((item) => {
                if (
                    item.propertyName &&
                    typeof product.representedProduct[item.propertyName] === 'boolean' &&
                    product.representedProduct[item.propertyName] === true
                ) {
                    labelsMap.set(intl.formatMessage(item.label), item.color)
                }
            })
        }
        return labelsMap
    }, [product, badgeDetails])

    const handleNotifyMeModalState = (stateValue) => {
        setIsNotifyMeModalOpen(stateValue)
    }

    const handleShopSimilarModalState = (stateValue) => {
        setIsShopSimilarModalOpen(stateValue)
    }

    const showQuickView = (triggerElement) => {
        setConfirmationTrigger(triggerElement || null)
        if (isDesktop && tileMode !== 'quick-view') {
            setIsQuickViewOpen(true)
            return
        }

        if (isDesktop && tileMode === 'quick-view') {
            setMobileQuickViewOpenInternal(true)
            return
        }

        if (setMobileQuickViewOpen) {
            setQuickViewProduct(productId)
            setQuickViewWishlistInfo({
                wishlistCount: wishlistCount,
                isFavourite: isProductInWishlist
            })
            setMobileQuickViewOpen(true)
        } else {
            setMobileQuickViewOpenInternal(true)
        }
    }

    const variationValues = productWithFilteredVariants?.variationValues || []

    const variant = useVariationByAttributeValues(product, variationValues)

    const {handleCartItem} = useProductTileCart({
        product,
        variant,
        productId,
        id,
        isWishlistPage,
        removeItemFromWishlist,
        ref: tileRef,
        setIsQuickViewOpen,
        isDesktop,
        onAddToCartModalOpen,
        onAddToCartModalClose
    })

    const {handleMouseEnter, handleMouseLeave} = useProductTileHover({
        isOOS,
        isNotifyMeModalOpen,
        isShopSimilarModalOpen,
        setIsHovered,
        setIsQuickViewOpen,
        setMobileQuickViewOpenInternal,
        triggerFreshLikesFetch,
        tileRef
    })
    const {tileMode: hookTileMode, isTouch} = useProductTileMode({
        tileRef,
        productId: productId || id
    })
    /* GDI carousel can override the screensize-based mode picker. When the
     * caller passes `forceTileMode` (string or Chakra responsive object), we
     * resolve to the active breakpoint and prefer it over the hook result.
     * PLP / recommendations / wishlist leave this unset → hook wins as before. */
    const resolvedForceTileMode = useBreakpointValue(
        typeof forceTileMode === 'object' && forceTileMode
            ? forceTileMode
            : forceTileMode
            ? {base: forceTileMode}
            : {}
    )
    const tileMode = resolvedForceTileMode || hookTileMode

    const PRODUCT_IMGS_RESIZE_PARAMS_ENABLED = getSiteEnvVarValue(appConfig, 'PRODUCT_IMGS_RESIZE_PARAMS_ENABLED', site?.id) == 'true'

    const mainImageUrl = image[0]?.disBaseLink || image[0]?.link
    const mainImage = buildImageUrl(mainImageUrl, PRODUCT_IMGS_RESIZE_PARAMS_ENABLED)

    const hoveredImageUrl = image[1]?.disBaseLink || image[1]?.link
    const hoveredImage = hoveredImageUrl
        ? buildImageUrl(hoveredImageUrl, PRODUCT_IMGS_RESIZE_PARAMS_ENABLED)
        : null

    const mainImages = image
        .map((img) => {
            const url = img?.disBaseLink || img?.link
            return url ? buildImageUrl(url, PRODUCT_IMGS_RESIZE_PARAMS_ENABLED) : null
        })
        .filter(Boolean)

    // Mixed media sequence (images + videos) for carousel — only when allImages data is available
    const mediaItems = useMemo(() => {
        const garageEnabled = getSiteEnvVarValue(appConfig, 'ENABLE_PRODUCT_VIDEO_GARAGE', site?.id) === 'true'
        const dynamiteEnabled = getSiteEnvVarValue(appConfig, 'ENABLE_PRODUCT_VIDEO_DYNAMITE', site?.id) === 'true'

        // Gate by feature flag + brand (use brand context — product.brand is absent on search hits)
        if (isGarage && !garageEnabled) return null
        if (isDynamite && !dynamiteEnabled) return null
        if (!isGarage && !isDynamite) return null

        // imageGroups only available after Phase 2 enrichment loads — bail out until then
        if (!imageGroups?.length) return null

        return buildTileMediaItems({
            imageUrls: mainImages,
            imageGroups,
            colorValue: colorValue || product?.c_color,
            videoSequencePositions: product?.c_videoSequencePositions || null
        })
    }, [mainImages, imageGroups, colorValue, product?.c_color, product?.c_videoSequencePositions, appConfig, isGarage, isDynamite])
    const showHoverImage =
        hoveredImage &&
        isHydrated() &&
        !isQuickViewOpen &&
        !(isProductTileMediaCarouselEnabled && hoveredImage && isDesktop)

    const promotionIds = getProductPromotionIds(productWithFilteredVariants)
    const {data} = usePromotions(
        {
            parameters: {
                ids: promotionIds.join(',')
            }
        },
        {
            enabled: promotionIds.length > 0
        }
    )
    const productPromotionObject = data?.data?.find((promo) => promo.c_showCalloutMessage === true)

    const onQuickViewCloseHandler = () => {
        setIsHovered(false)
        setQvLocalSelections({})
    }

    const closeInternalMobileQuickView = () => {
        setMobileQuickViewOpenInternal(false)
        onQuickViewClose?.()
    }

    const styles = useMultiStyleConfig('ProductTile', {
        variant: tileMode === 'quick-view' ? 'QuickView' : stylingVariant,
        tileMode,
        isDesktop,
        isHovered,
        isTouch,
        showHoverImage,
        disableImageScroll,
        isOOS,
        isQuickViewOpen,
        showScrollHint
    })

    const productTileMediaProps = {
        styles,
        productObject,
        showVideo,
        videoProps,
        responsiveMediaBlock,
        isLayout,
        showHoverImage,
        enableImageCarousel: isProductTileMediaCarouselEnabled,
        isDesktop,
        mainImages,
        mediaItems,
        mainImage,
        hoveredImage,
        dynamicImageProps,
        shouldSkipQuickView: ShouldSkipQuickView,
        showQV,
        isGiftCard,
        isHydrated: isHydrated(),
        isQuickViewOpen,
        handleNotifyMeModalState,
        handleShopSimilarModalState,
        setIsQuickViewOpen,
        productId,
        setColorValue,
        setProductId,
        onQuickViewCloseHandler,
        isWishlistPage,
        wishlistProductId,
        hasLengthVariant,
        product,
        setSelectedVariantId,
        setQvProduct,
        showQuickView,
        tileMode,
        handleCartItem,
        isHovered,
        responsiveDisplay,
        onLocalSelectionsChange: setQvLocalSelections
    }
    const productTileDetailsProps = {
        styles,
        localizedProductName,
        isShopTheLook,
        showSwatches,
        product: productObject,
        isWishlistEnabled,
        showProductLikes,
        isProductInWishlist,
        wishlistCount,
        isHovered,
        handleFavouriteClick,
        isLoadingFreshLikes,
        isWishlistPage,
        productWithFilteredVariants,
        isRefreshingData: isRefreshingData || isSelectedTileProductPriceLoading,
        priceData,
        currency,
        productPromotionObject,
        isOOS,
        intl,
        tooltipState: {isOpen, onOpen, onClose, onToggle},
        colorValue,
        setColorValue,
        setProductId,
        productId,
        isGiftCard,
        responsiveDisplay
    }

    return (
        <>
            <Box
                {...styles.container}
                {...rest}
                data-product-tile="true"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onFocus={handleMouseEnter}
                ref={setTileRef}
            >
                <Link data-testid="product-tile" to={productUrl} {...styles.link} onClick={onClick}>
                    {/* Product tile media */}
                    <ProductTileMedia {...productTileMediaProps} />
                    {showDetails && <ProductTileDetails {...productTileDetailsProps} />}
                </Link>
                {enableFavourite && isHydrated() && !hasTileVideo && !isGiftCard && (
                    <Box
                        sx={responsiveDisplay?.wishlistIcon}
                        onClick={(e) => {
                            e.preventDefault()
                        }}
                    >
                        <IconButtonWithRegistration
                            as={ChakraLink}
                            href={'#'}
                            data-testid="wishlist-button"
                            aria-label={
                                isProductInWishlist
                                    ? intl.formatMessage(
                                          {
                                              id: 'product_tile.assistive_msg.remove_from_wishlist',
                                              defaultMessage: 'Remove {product} from wishlist'
                                          },
                                          {product: localizedProductName}
                                      )
                                    : intl.formatMessage(
                                          {
                                              id: 'product_tile.assistive_msg.add_to_wishlist',
                                              defaultMessage: 'Add {product} to wishlist'
                                          },
                                          {product: localizedProductName}
                                      )
                            }
                            icon={
                                isProductInWishlist ? (
                                    <HeartSolidIcon {...styles.favIconSolid} />
                                ) : (
                                    <HeartIcon
                                        display={!isHovered && isDesktop ? 'none' : 'inline'}
                                        {...styles.favIconOutline}
                                    />
                                )
                            }
                            {...styles.favIcon}
                            {...styles.favIconButton}
                            onClick={() => {
                                handleFavouriteClick()
                            }}
                        />
                    </Box>
                )}
            </Box>

            {/* QuickView */}
            {(!setMobileQuickViewOpen || (isDesktop && tileMode === 'quick-view')) && (
                <ProductTileMobileQuickView
                    isOpen={mobileQuickViewOpen}
                    onClose={closeInternalMobileQuickView}
                    productId={productId}
                    isProductInWishlist={isProductInWishlist}
                    wishlistCount={wishlistCount}
                    setProductId={setProductId}
                    isWishlistPage={isWishlistPage}
                    wishlistProductId={wishlistProductId}
                    product={product}
                    onVariantSelect={setSelectedVariantId}
                    onProductChange={setQvProduct}
                    onLocalSelectionsChange={setQvLocalSelections}
                    setIsQuickViewOpen={setMobileQuickViewOpenInternal}
                    onQuickViewClose={onQuickViewClose}
                    quickViewVariant={tileMode === 'quick-view' ? 'QuickView' : undefined}
                    forceMobileLayout={isDesktop && tileMode === 'quick-view'}
                    placement={isDesktop && tileMode === 'quick-view' ? 'right' : 'bottom'}
                    motionPreset={
                        isDesktop && tileMode === 'quick-view' ? 'slideInRight' : 'slideInBottom'
                    }
                    size={isDesktop && tileMode === 'quick-view' ? 'md' : 'sm'}
                    height={isDesktop && tileMode === 'quick-view' ? undefined : '85vh'}
                    isFullHeight={isDesktop && tileMode === 'quick-view'}
                />
            )}
        </>
    )
})

ProductTile.displayName = 'ProductTile'

ProductTile.propTypes = productTilePropTypes

export default ProductTile
